import { useEffect, useRef, useState } from "react";
import { Globe, Lock, Link2, Eye } from "lucide-react";
import { Button, Field, FormDialog, Icon } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import {
  useFanClubSettings,
  useAnnouncements,
  useAnnouncementForEdit,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  usePublishAnnouncement,
} from "../../app/hooks/useAnnouncements.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  ANNOUNCEMENT_TYPES,
  BODY_MAX,
  EVERYONE_TIER_ID,
  audienceLabel,
  audienceOptions,
  typeMeta,
  type AnnouncementType,
} from "./announcementMeta.js";
import { AnnouncementLoading } from "./AnnouncementStates.js";
import styles from "./AnnouncementComposerModal.module.scss";

type CreateAnnouncementRequest = components["schemas"]["CreateAnnouncementRequest"];

export interface AnnouncementComposerModalProps {
  /** Present ⇒ edit an existing draft; absent ⇒ compose a new announcement. */
  announcementId?: string;
  onClose: () => void;
}

/**
 * The Antenna announcements composer (routed modal `/antenna/announcements/new` + `/:id/edit`;
 * shot-43). Drafts, then publishes, against the backend. Antenna-branded: the Publish CTA is
 * the crimson `accent` (the one sanctioned BRAND-3 exception for a fan-facing CTA).
 *
 * Publish/retract are NEVER optimistic: an explicit pending state, server confirmation, the server
 * error surfaced verbatim inline, and NO success on an unpersisted write (the mutation throws → the modal
 * stays open with the error). Publish carries an Idempotency-Key generated once per publish intent
 * and REUSED across retries — and the created draft id is held so a retry never creates a second draft.
 *
 * Degrades (all pre-A4/Phase-4, F-06): per-tier live counts are absent (band-metrics is A4) and the release
 * picker is disabled with a note (release events are core Phase 4, sync point 3). Edit hydrates the full draft
 * from cache (there is no GET-single, an decision); an un-cached draft shows a reopen notice rather than
 * risk a partial save that blanks the body/links.
 */
export function AnnouncementComposerModal({ announcementId, onClose }: AnnouncementComposerModalProps) {
  const isEdit = announcementId != null;
  // Composer authoring is antenna:manage. The hub already hides the entry for a read-only seat, but a
  // direct-URL nav must not render the form either — gate it here too (capabilities-as-data; the server
  // 403 is the real enforcement, this is the client mirror).
  const canManage = useCapability("antenna:manage");
  const settings = useFanClubSettings();
  const list = useAnnouncements(); // the edit-hydration + summary-fallback source
  const edit = useAnnouncementForEdit(announcementId ?? "");
  const createMut = useCreateAnnouncement();
  const updateMut = useUpdateAnnouncement();
  const publishMut = usePublishAnnouncement();

  const [type, setType] = useState<AnnouncementType>("single");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audienceTierId, setAudienceTierId] = useState<string>(EVERYONE_TIER_ID);
  const [persistedId, setPersistedId] = useState<string | undefined>(announcementId);
  const [busy, setBusy] = useState<"idle" | "saving" | "publishing">("idle");
  const [actionError, setActionError] = useState<string | null>(null);
  const [titleInvalid, setTitleInvalid] = useState(false);

  // Sticky once the edit form has hydrated from the cached full view — a later cache eviction (the announcement
  // key has no query observer, so it can be GC'd) must not yank the form out from under the author. New mode is
  // ready immediately. The preserved (non-UI) fields are captured HERE so buildPayload never re-reads the live
  // cache and thus can never blank body/cities/links on a save.
  const [hydrated, setHydrated] = useState(!isEdit);
  const preservedRef = useRef({
    linkUrl: null as string | null,
    releaseId: null as string | null,
    artMediaId: null as string | null,
    cities: [] as string[],
    festivalEveryone: false,
  });
  const publishKeyRef = useRef<{ id: string; key: string } | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // The form is usable in new mode, or in edit mode once the full draft view has hydrated (created/edited this
  // session). An un-cached edit target shows a reopen notice instead — never a partial save (no GET-single).
  const editFormReady = hydrated;

  // Hydrate the form ONCE from the cached full view (faithful body/cities/links). Runs when the view resolves.
  useEffect(() => {
    if (!isEdit || hydrated) return;
    const v = edit.view;
    if (!v) return;
    setType((v.type as AnnouncementType) ?? "single");
    setTitle(v.title ?? "");
    setBody(v.body ?? "");
    setAudienceTierId(v.audienceTierId ?? EVERYONE_TIER_ID);
    setPersistedId(v.announcementId);
    preservedRef.current = {
      linkUrl: v.linkUrl ?? null,
      releaseId: v.releaseId ?? null,
      artMediaId: v.artMediaId ?? null,
      cities: v.cities ?? [],
      festivalEveryone: v.festivalEveryone ?? false,
    };
    setHydrated(true);
  }, [isEdit, hydrated, edit.view]);

  // `FormDialog` owns Escape, the focus trap and focus restore; it focuses its first focusable (the close
  // button), so claim the headline — the real entry point — once the form is ready and the field exists.
  useEffect(() => {
    if (editFormReady) titleRef.current?.focus();
  }, [editFormReady]);

  const options = audienceOptions(settings.data?.tiers);

  function buildPayload(): CreateAnnouncementRequest {
    // Preserve the draft's non-UI fields (cities/links) across an edit so a save never blanks them; a new
    // draft carries the empty defaults. Captured at hydration (preservedRef), never re-read from the live cache.
    // The composer itself never authors cities — Antenna geocodes them (F-06 degrade).
    const p = preservedRef.current;
    return {
      type,
      title: title.trim(),
      body: body.trim() ? body.trim() : null,
      audienceTierId: audienceTierId || null,
      linkUrl: p.linkUrl,
      releaseId: p.releaseId,
      artMediaId: p.artMediaId,
      cities: p.cities,
      festivalEveryone: p.festivalEveryone,
    };
  }

  function validate(): boolean {
    if (!title.trim()) {
      setTitleInvalid(true);
      setActionError("Add a headline before you save or publish.");
      titleRef.current?.focus();
      return false;
    }
    return true;
  }

  /** Ensure a persisted draft id, creating one on first save. Returns the id (throws to the caller on failure). */
  async function ensureDraft(): Promise<string> {
    const payload = buildPayload();
    if (persistedId) {
      await updateMut.mutateAsync({ id: persistedId, body: payload });
      return persistedId;
    }
    const view = await createMut.mutateAsync(payload);
    const id = view.announcementId;
    if (!id) throw new Error("The server didn't return the new announcement id.");
    setPersistedId(id);
    return id;
  }

  async function handleSaveDraft() {
    if (!validate()) return;
    setBusy("saving");
    setActionError(null);
    try {
      await ensureDraft();
      onClose();
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't save the draft. Please try again."));
    } finally {
      setBusy("idle");
    }
  }

  async function handlePublish() {
    if (!validate()) return;
    setBusy("publishing");
    setActionError(null);
    try {
      const prior = publishKeyRef.current;
      if (prior && prior.id === persistedId) {
        // RETRY of a publish already attempted for THIS draft: do NOT re-run the pre-publish create/update.
        // If the first publish persisted server-side and only its 202 was lost to a blip, a pre-publish PUT
        // would 409 on the now-published row and this reused-key replay would never fire — so re-issue publish
        // directly with the SAME Idempotency-Key and let the backend replay the stored 202. It is
        // equally safe when the first publish never persisted (a genuine 500 released the key → this re-claims).
        await publishMut.mutateAsync({ id: prior.id, idempotencyKey: prior.key });
      } else {
        // FIRST publish attempt for this draft: fold in the current form (create, or update a hydrated draft),
        // mint a fresh key, then publish.
        const id = await ensureDraft();
        const idempotencyKey = crypto.randomUUID();
        publishKeyRef.current = { id, key: idempotencyKey };
        await publishMut.mutateAsync({ id, idempotencyKey });
      }
      publishKeyRef.current = null;
      onClose();
    } catch (err) {
      setActionError(errorMessage(err, "Couldn't publish. Please try again."));
    } finally {
      setBusy("idle");
    }
  }

  const audienceName = audienceLabel(audienceTierId, settings.data?.tiers);

  return (
    <FormDialog
      title={isEdit ? "Edit announcement" : "New announcement"}
      closeLabel="Close composer"
      size="lg"
      onClose={onClose}
      footer={
        canManage && editFormReady ? (
          <>
            <Button
              variant="ghost"
              onClick={() => void handleSaveDraft()}
              loading={busy === "saving"}
              disabled={busy !== "idle"}
            >
              Save draft
            </Button>
            <Button
              variant="accent"
              onClick={() => void handlePublish()}
              loading={busy === "publishing"}
              disabled={busy !== "idle"}
            >
              Publish
            </Button>
          </>
        ) : undefined
      }
    >
      {!canManage ? (
        <div className={styles.notice} role="note">
          <p className={styles.noticeTitle}>Not available for your role</p>
          <p className={styles.noticeText}>
            Your role on this band doesn&apos;t include posting to Antenna.
          </p>
          <Button variant="ghost" onClick={onClose}>
            Back to fan club
          </Button>
        </div>
      ) : !editFormReady ? (
        list.isLoading ? (
          <AnnouncementLoading label="Loading draft…" />
        ) : (
          <div className={styles.notice} role="note">
            <p className={styles.noticeTitle}>This draft isn&apos;t loaded here</p>
            <p className={styles.noticeText}>
              Open it from your fan club to edit the full message.
            </p>
            <Button variant="ghost" onClick={onClose}>
              Back to fan club
            </Button>
          </div>
        )
      ) : (
        <>
          {/* WHAT IS IT? — type chips (single-select), shot-43 order + labels. */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.eyebrow}>What is it?</legend>
            <div className={styles.chips} role="radiogroup" aria-label="Announcement type">
              {ANNOUNCEMENT_TYPES.map((t) => {
                const meta = typeMeta(t);
                const selected = type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={styles.chip}
                    data-selected={selected || undefined}
                    onClick={() => setType(t)}
                  >
                    <Icon icon={meta.icon} size={16} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Release picker — DEGRADED (Phase 4 / sync point 3): disabled, no options exist yet. */}
          <div className={styles.fieldset}>
            <p className={styles.eyebrow}>Link from Artist CNTRL · Releases</p>
            <div className={styles.releasePicker} aria-disabled="true">
              <Icon icon={Link2} size={16} />
              <span>Release links arrive in a later update — publish without one for now.</span>
            </div>
          </div>

          {/* HEADLINE */}
          <Field
            ref={titleRef}
            label="Headline"
            placeholder="e.g. New single, “Saltwater”"
            value={title}
            error={titleInvalid ? "Add a headline." : undefined}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleInvalid) setTitleInvalid(false);
            }}
          />

          {/* MESSAGE + 0/280 counter */}
          <div className={styles.messageField}>
            <div className={styles.messageLabelRow}>
              <label htmlFor="announcement-body" className={styles.fieldLabel}>
                Message
              </label>
              <span
                className={styles.counter}
                data-max={body.length >= BODY_MAX || undefined}
                aria-label={`${body.length} of ${BODY_MAX} characters`}
              >
                {body.length}/{BODY_MAX}
              </span>
            </div>
            <textarea
              id="announcement-body"
              className={styles.textarea}
              rows={4}
              maxLength={BODY_MAX}
              placeholder="A couple sentences straight to your fans — what it is and why they should tap."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          {/* WHO SEES IT — audience cards from settings tiers; live counts DEGRADE (no counts, A4). */}
          <fieldset className={styles.fieldset}>
            <legend className={styles.eyebrow}>Who sees it</legend>
            <div className={styles.audience} role="radiogroup" aria-label="Audience">
              {options.map((opt) => {
                const selected = audienceTierId === opt.id;
                return (
                  <button
                    key={opt.id || "everyone"}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={styles.audienceCard}
                    data-selected={selected || undefined}
                    onClick={() => setAudienceTierId(opt.id)}
                  >
                    <span className={styles.audienceName}>
                      <Icon icon={opt.locked ? Lock : Globe} size={16} />
                      {opt.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Who this reaches, as the last line of the form — it sat at the left of the old footer, but the footer is
              the actions alone now (on a phone they split the row, and a sentence cannot share it). */}
          <p className={styles.reach}>
            <Icon icon={Eye} size={16} />
            <span>
              {audienceTierId === EVERYONE_TIER_ID
                ? "All your fans will see this"
                : `${audienceName} members will see this`}
            </span>
          </p>

          {actionError && (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          )}
        </>
      )}
    </FormDialog>
  );
}
